import { apiClient } from './apiClient';

/**
 * Client-facing usage in credits. Distinct from the operator Usage & Cost page:
 * this never carries a raw provider dollar cost, only credits. Credits count
 * all runs (failures included, ratified); completed/failed are reported so the
 * UI can hide failures by default and let the client filter them in.
 */
export type PieceStatus = 'completed' | 'failed' | 'running';

export interface CreditsDailyPoint {
  /** Day total = completed + failed. */
  date: string;
  credits: number;
  /** Credits from completed runs that day. */
  completed: number;
  /** Credits from non-completed runs that day ("not completed"). */
  failed: number;
}

export interface CreditsRecentPiece {
  title: string;
  credits: number;
  status: PieceStatus;
  date: string;
}

export interface CreditsCycleOption {
  /** "2026-07" */
  value: string;
  /** "July 2026" */
  label: string;
}

export interface UsageCredits {
  tenant_id: string;
  period_start: string;
  period_end: string;
  credits_used: number;
  completed_count: number;
  failed_count: number;
  /** One credit ≈ this many USD of underlying cost (published for context). */
  credit_dollar_value: number;
  /** The cycle this payload covers, as "YYYY-MM". */
  cycle: string;
  daily: CreditsDailyPoint[];
  recent: CreditsRecentPiece[];
  /** Every month with a run, newest first — drives the cycle picker. */
  available_cycles: CreditsCycleOption[];
}

/**
 * @param month optional cycle to report ("YYYY-MM"); omit for the current month.
 */
export async function getUsageCredits(
  tenantId: string,
  month?: string,
): Promise<UsageCredits> {
  const { data } = await apiClient.get<UsageCredits>('/usage/credits', {
    params: { tenant_id: tenantId, ...(month ? { month } : {}) },
  });
  // Normalize so callers (page render AND the exporters) never touch an
  // undefined array — a backend not yet redeployed with the newer fields
  // omits `cycle`/`available_cycles`, and older ones could omit the series.
  return {
    ...data,
    cycle: data.cycle ?? '',
    daily: data.daily ?? [],
    recent: data.recent ?? [],
    available_cycles: data.available_cycles ?? [],
  };
}
